import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "./style.css";
import fire from "../../config/Fire";
import { FEATURES } from "../../config/features";
import { useAuth, roleHome } from "../../auth/AuthProvider";
import { shellHome } from "../../appshell/navConfig";
import { Button, Field, Input, PasswordInput } from "../../ui";

/**
 * The rebuilt login page (REDESIGN-RUNBOOK.md §3.3): one page, no modal twin,
 * inline forgot-password, real error states. "Create Account" is gone —
 * public signup is intentionally disabled. Post-login you land where you
 * were headed (?next= from the 401 redirect, or router state.from), else on
 * your role's home. The forgot-password confirmation is deliberately the
 * same whether or not the account exists (no account enumeration).
 */

function friendlyAuthError(error) {
  switch (error && error.code) {
    case "auth/invalid-email":
      return "That doesn't look like a valid email address.";
    case "auth/user-not-found":
    case "auth/wrong-password":
    case "auth/invalid-credential":
    case "auth/invalid-login-credentials":
      return "That email and password don't match our records.";
    case "auth/too-many-requests":
      return "Too many attempts. Wait a few minutes, or reset your password below.";
    case "auth/network-request-failed":
      return "We couldn't reach the sign-in service — check your connection and try again.";
    default:
      return "Something went wrong signing you in. Please try again.";
  }
}

function LoginPage() {
  const { authReady, user, role, roleReady, tier, rateLimited, retryAt, retryRole } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const [mode, setMode] = useState("login"); // "login" | "forgot" | "forgot-sent" | "forgot-held"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const params = new URLSearchParams(location.search);
  const nextParam = params.get("next");
  // Only ever redirect within the app — never to an absolute/external URL.
  const safeNext =
    nextParam && nextParam.startsWith("/") && !nextParam.startsWith("//") ? nextParam : null;
  const from = (location.state && location.state.from) || safeNext;
  const arrivedFromBounce = Boolean(from);

  // Already signed in (or just signed in): leave as soon as the role settles.
  useEffect(() => {
    if (!authReady || !user) {
      return;
    }
    if (roleReady && role) {
      // Cutover (Evan GO, mini-gate): the redesign tiers (referral/writing/admin
      // → a non-null tier) land in the /app workspace via shellHome; clients and
      // any unmapped role keep the legacy dashboard. ?next=/state.from still wins.
      navigate(from || (tier ? shellHome(tier) : roleHome(role)), { replace: true });
      return;
    }
    // Role resolution finished with no role (unlinked), or the backend is
    // rate-limiting: either way the login attempt is over — stop the spinner
    // so the user sees the state instead of a stuck button.
    if ((roleReady && !role) || rateLimited) {
      setSubmitting(false);
    }
  }, [authReady, user, role, roleReady, tier, rateLimited, navigate, from]);

  // A 429 on the role probe is transient — surface it as "busy", never as
  // "not linked", and never sign the user out (E integration P1).
  const systemBusy = authReady && user && rateLimited;
  const unlinkedAccount = authReady && user && roleReady && !role && !rateLimited;

  // Honest "retrying in Xs" countdown to the next auto-retry (Retry-After).
  const [nowTs, setNowTs] = useState(() => Date.now());
  useEffect(() => {
    if (!systemBusy || !retryAt) {
      return undefined;
    }
    const id = window.setInterval(() => setNowTs(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [systemBusy, retryAt]);
  const secondsLeft =
    systemBusy && retryAt ? Math.max(0, Math.ceil((retryAt - nowTs) / 1000)) : null;

  const submitLogin = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) {
      setError("Enter your email and password to log in.");
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      await fire.auth().signInWithEmailAndPassword(email.trim(), password);
      // Success: the AuthProvider picks up the session and the effect above
      // redirects once the role resolves. Keep the button in its busy state.
    } catch (err) {
      setError(friendlyAuthError(err));
      setSubmitting(false);
    }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      setError("Enter your email address and we'll send a reset link.");
      return;
    }
    setError("");
    // Pre-launch email rule (Evan, 2026-07-11): until FEATURES.emailsLive,
    // send NOTHING from here — Firebase reset emails bypass the backend
    // MAIL_ALLOWLIST, so this is the enforcement point. Sole exception:
    // evan@fawncircle.com exactly (his own testing). No Firebase call is
    // made at all in the held path, so it is enumeration-safe by
    // construction and the copy stays honest (no pretend "email sent").
    const canSend = FEATURES.emailsLive || email.trim().toLowerCase() === "evan@fawncircle.com";
    if (!canSend) {
      setMode("forgot-held");
      return;
    }
    setSubmitting(true);
    try {
      await fire.auth().sendPasswordResetEmail(email.trim());
    } catch (err) {
      // Deliberately swallow user-not-found: the confirmation below is the
      // same either way, so this page can't be used to test for accounts.
      if (err && err.code === "auth/invalid-email") {
        setError("That doesn't look like a valid email address.");
        setSubmitting(false);
        return;
      }
      if (err && err.code === "auth/network-request-failed") {
        setError("We couldn't reach the sign-in service — check your connection and try again.");
        setSubmitting(false);
        return;
      }
    }
    setSubmitting(false);
    setMode("forgot-sent");
  };

  const signOutUnlinked = async () => {
    await fire.auth().signOut();
    setError("");
    setSubmitting(false);
  };

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-brand">
          <span className="login-brand__mark" aria-hidden="true">
            F
          </span>
          <span className="login-brand__name">Fawn Circle Insurance</span>
        </div>

        {mode === "login" ? (
          <>
            <h1 className="login-title">Log in to Fawn Online</h1>

            {arrivedFromBounce && !error && !unlinkedAccount && !systemBusy ? (
              <p className="login-notice" role="status">
                You were signed out — log in to pick up where you left off.
              </p>
            ) : null}

            {systemBusy ? (
              <div className="login-notice" role="status">
                The server's busy right now —{" "}
                {secondsLeft && secondsLeft > 0 ? `retrying in ${secondsLeft}s` : "retrying"}.
                You're still signed in; nothing's wrong with your account.
                <div style={{ marginTop: "var(--space-3)" }}>
                  <Button size="sm" onClick={retryRole}>
                    Try again now
                  </Button>
                </div>
              </div>
            ) : null}

            {unlinkedAccount ? (
              <div className="login-error" role="alert">
                This account isn't linked to an agency profile. Contact your agency administrator.
                <div style={{ marginTop: "var(--space-3)" }}>
                  <Button size="sm" onClick={signOutUnlinked}>
                    Use a different account
                  </Button>
                </div>
              </div>
            ) : null}

            <form onSubmit={submitLogin} noValidate>
              <Field label="Email" htmlFor="login-email">
                <Input
                  id="login-email"
                  type="email"
                  name="email"
                  autoComplete="username"
                  autoFocus
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (error) {
                      setError(""); // E QA: don't let a prior error linger while retrying
                    }
                  }}
                  disabled={submitting}
                />
              </Field>
              <Field label="Password" htmlFor="login-password">
                <PasswordInput
                  id="login-password"
                  name="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (error) {
                      setError("");
                    }
                  }}
                  disabled={submitting}
                />
              </Field>

              {error ? (
                <p className="login-error" role="alert">
                  {error}
                </p>
              ) : null}

              <Button type="submit" variant="primary" loading={submitting} className="login-submit">
                Log in
              </Button>
            </form>

            <button
              type="button"
              className="login-link"
              onClick={() => {
                setMode("forgot");
                setError("");
              }}
            >
              Forgot your password?
            </button>

            <p className="login-footnote">Need an account? Contact your agency administrator.</p>
          </>
        ) : mode === "forgot" ? (
          <>
            <h1 className="login-title">Reset your password</h1>
            <p className="login-subtitle">
              Enter the email you use for Fawn Online and we'll send a reset link.
            </p>
            <form onSubmit={submitForgot} noValidate>
              <Field label="Email" htmlFor="forgot-email">
                <Input
                  id="forgot-email"
                  type="email"
                  name="email"
                  autoComplete="username"
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={submitting}
                />
              </Field>

              {error ? (
                <p className="login-error" role="alert">
                  {error}
                </p>
              ) : null}

              <Button type="submit" variant="primary" loading={submitting} className="login-submit">
                Send reset link
              </Button>
            </form>
            <button
              type="button"
              className="login-link"
              onClick={() => {
                setMode("login");
                setError("");
              }}
            >
              Back to log in
            </button>
          </>
        ) : mode === "forgot-held" ? (
          <>
            <h1 className="login-title">Password help</h1>
            <p className="login-subtitle" role="status">
              While Fawn Online gets ready for launch, password resets are handled personally —
              contact Evan at{" "}
              <a className="login-inline-link" href="mailto:evan@fawncircle.com">
                evan@fawncircle.com
              </a>{" "}
              and he'll get you back in.
            </p>
            <Button
              variant="secondary"
              className="login-submit"
              onClick={() => {
                setMode("login");
                setError("");
              }}
            >
              Back to log in
            </Button>
          </>
        ) : (
          <>
            <h1 className="login-title">Check your email</h1>
            <p className="login-subtitle" role="status">
              If that address has an account, we've emailed a password reset link. It can take a
              minute to arrive — check your spam folder too.
            </p>
            <Button
              variant="secondary"
              className="login-submit"
              onClick={() => {
                setMode("login");
                setError("");
              }}
            >
              Back to log in
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

export default LoginPage;
